import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useLocation, useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Container, Grid, MenuItem, Select } from "@mui/material";
import logo from "./../../assets/safe_logo.png";
import RoundButton from "../../components/RoundButton";
import { useDispatch, useSelector } from "react-redux";
import {
  checkUserExistsbyemail,
  phoneValidationRequest,
  signUpRequestFromG2Portal,
} from "../../redux/signup/action";
import { updateSignUpFrom } from "../../redux/signup/constants";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import createAccountScheme from "../../validations/createAccountScheme";
import codeMessage from "../../services/apiManager/httpMessages";
import CustomLoader from "../../components/CustomLoader";
import { enqueueSnackbar } from "notistack";
import background_image from "./../../assets/png/background_image.png";
import { useResponsive } from "../../hooks/useResponsive";
import storePersist, { localConstant } from "../../services/localStorage";
import SnackbarConfig from "../../utils/snackbarConfig";
import { g2OperatorSignupParams } from "../../redux/operator/action";
import { useTranslation } from "react-i18next";
import { COUNTRY_CODE, DEFAULT_COUNTRY_CODE } from "../../utils/utils";
import { isMobileDeviceCheck } from "../../utils";
import { KEYS } from "../../constants";

export const PasswordChecklist = ({ password }) => {
  const { t } = useTranslation();
  const passwordChecklistItems = [
    { label: t("pages.signup.text10"), validate: /.{8,}$/ },
    { label: t("pages.signup.text11"), validate: /[A-Z]/ },
    { label: t("pages.signup.text12"), validate: /\d/ },
    { label: t("pages.signup.text13"), validate: /[!@#$%^&*()\-_=+]/ },
  ];

  return (
    <Stack>
      <Typography
        color={"gray"}
        variant="caption"
        style={{
          fontWeight: "bold",
          textTransform: "uppercase",
        }}
        alignItems="center"
        justifyContent="center"
      >
        {t("pages.signup.text09")}
      </Typography>
      <Grid container columnSpacing={1}>
        {passwordChecklistItems.map((item, index) => (
          <Grid item xs={12} sm={6} lg={6} key={index}>
            <Typography
              variant="subtitle2"
              color={
                !!password
                  ? item.validate.test(password)
                    ? "green"
                    : "red"
                  : "gray"
              }
              alignItems="center"
              justifyContent="center"
              fontWeight={"unset"}
            >
              • {item.label}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

const SignUpPage = () => {
  const { isLoading, createAccount, g2CustomerOrUserSignupParams } =
    useSelector((state) => state.signUpReducer);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { newUser = "" } = location.state || {};
  const smDown = useResponsive("down", "sm");
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm({
    mode: "all",
    defaultValues: {
      phoneNumber: undefined,
      userEmail: undefined,
      countryCode: DEFAULT_COUNTRY_CODE,
    },
    resolver: yupResolver(createAccountScheme),
  });

  const password = useWatch({ control, name: "password" });

  const submitForm = (values) => {
    if (g2CustomerOrUserSignupParams?.flowtype) {
      const g2SignUpPrams = {
        ...values,
        ...g2CustomerOrUserSignupParams,
      };

      const newG2SignUpPrams = {
        ...g2SignUpPrams,
        phone: {
          phoneNumber: g2SignUpPrams?.phoneNumber,
          intlDialCd: g2SignUpPrams?.countryCode,
        },
      };

      delete newG2SignUpPrams[KEYS.PHONE_NUMBER];
      delete newG2SignUpPrams[KEYS.COUNTRY_CODE];

      dispatch(
        signUpRequestFromG2Portal(newG2SignUpPrams, (g2SignupResponse) => {
          if (g2SignupResponse.statusCode === 201) {
            storePersist.clear();
            enqueueSnackbar(g2SignupResponse.message, {
              variant: "success",
              autoHideDuration: 3000,
              action: SnackbarConfig,
            });
            dispatch(g2OperatorSignupParams({}));
            navigate("/login");
          } else {
            enqueueSnackbar(g2SignupResponse.message, { variant: "error" });
          }
        }),
      );
    } else {
      dispatch(
        checkUserExistsbyemail({ email: values?.userEmail }, (response) => {
          if (response?.statusCode !== 200) {
            enqueueSnackbar(response?.message, {
              variant: "error",
              autoHideDuration: 3000,
              action: SnackbarConfig,
            });
          } else {
            if (values?.phoneNumber?.length > 8)
              dispatch(
                phoneValidationRequest(
                  values?.phoneNumber,
                  values?.countryCode,
                  {},
                  (response) => {
                    if (response?.message === codeMessage[400]) {
                      setError("phoneNumber", {
                        type: "manual",
                        message: "Invalid phone number",
                      });
                    } else {
                      dispatch(updateSignUpFrom(values));
                      storePersist.set(
                        localConstant.OWNER_SIGN_UP_DETAILS,
                        values,
                      );
                      navigate("/companyDetailsPage");
                    }
                  },
                ),
              );
          }
        }),
      );
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  return (
    <Container
      disableGutters
      maxWidth={false}
      style={{
        paddingTop: 20,
        position: "relative",
        minHeight: "100vh",
        backgroundColor: "#80BCFF4F",
        backgroundImage: `url(${background_image})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        height: "100vh",
      }}
    >
      <Box
        maxWidth="xs"
        sx={{
          flexGrow: 1,
          flexDirection: "column",
          mb: 2,
          display: "flex",

          height: 1,
          overflow: "hidden",
          overflowY: "scroll",
          minHeight: "600px",
        }}
      >
        <Box
          sx={{
            height: 1,
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            flexDirection: ["column", "column", "row"],
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: 1, width: 1, padding: 0, gap: 5 }}
          >
            <img src={logo} alt="Your Alt Text" />
            <Card
              sx={{
                p: 3,
                width: 1,
                maxWidth: 600,
                height: smDown ? 1 : "auto",
              }}
              style={{ overflow: "auto" }}
            >
              <form onSubmit={handleSubmit((data) => submitForm(data))}>
                <Stack spacing={2}>
                  <Typography variant="titleText" color="primary.main">
                    {t("pages.signup.pageHeading")}
                  </Typography>
                  <Controller
                    name="userEmail"
                    control={control}
                    defaultValue={g2CustomerOrUserSignupParams?.email}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("pages.signup.text01")}
                        name="email"
                        type="email"
                        error={!!errors.userEmail}
                        helperText={errors.userEmail?.message}
                        disabled={
                          g2CustomerOrUserSignupParams?.email ? true : false
                        }
                      />
                    )}
                  ></Controller>
                  <Grid
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                  >
                    <Grid>
                      <Controller
                        name="countryCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            renderValue={(value) =>
                              value?.length
                                ? Array.isArray(value)
                                  ? value.join(", ")
                                  : value
                                : "Country Code"
                            }
                            sx={{
                              width: isMobileDeviceCheck() ? 80 : 100,
                            }}
                            labelId="demo-simple-select-autowidth-label"
                            id="demo-simple-select-autowidth"
                            autoWidth
                            label="Country Code"
                            {...field}
                          >
                            {Object.keys(COUNTRY_CODE).map((item) => {
                              return (
                                <MenuItem value={item}>
                                  {COUNTRY_CODE[item]}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      ></Controller>
                      <p style={{ color: "#d32f2f", fontSize: "12px" }}>
                        {errors.countryCode?.message}
                      </p>
                    </Grid>

                    <Grid width={"70%"}>
                      <Controller
                        name="phoneNumber"
                        control={control}
                        defaultValue={g2CustomerOrUserSignupParams?.phone}
                        render={({ field }) => (
                          <TextField
                            size={"medium"}
                            {...field}
                            style={{
                              width: "100%",
                            }}
                            name="phoneNumber"
                            type="tel"
                            label={t("pages.signup.text05")}
                            error={!!errors.phoneNumber}
                          />
                        )}
                      ></Controller>
                      <p style={{ color: "#d32f2f", fontSize: "12px" }}>
                        {errors.phoneNumber?.message}
                      </p>
                    </Grid>
                  </Grid>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        name="password"
                        label={t("pages.signup.text02")}
                        error={!!errors.password}
                        type={showPassword ? "text" : "password"}
                        inputProps={{ maxLength: 20 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleClickShowPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  ></Controller>
                  <PasswordChecklist password={password} />
                </Stack>
                <RoundButton
                  sx={{ mt: 5, mb: 2 }}
                  size="large"
                  fullWidth
                  variant="contained"
                  type="submit"
                  color="primary"
                  rounded
                >
                  <Typography variant="buttonText" color="common.buttonText">
                    {t("pages.signup.text08")}
                  </Typography>
                </RoundButton>
              </form>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                  color="primary.main"
                  variant="smallLinkText"
                  underline="hover"
                  onClick={() => {
                    navigate("/login");
                  }}
                >
                  {t("pages.signup.text07")}
                </Typography>
              </Stack>
            </Card>
          </Stack>
        </Box>
      </Box>
      {isLoading && <CustomLoader />}
    </Container>
  );
};

export default SignUpPage;
